import React, { useContext, useMemo, useState, forwardRef, useCallback, useEffect, useRef } from 'react';
import { 
  Box,
  Text,
  Stack,
  Group,
  Select,
  Table,
  Drawer,
  CloseButton,
  ScrollArea,
  Tooltip,
  LoadingOverlay,
  Button,
  ActionIcon,
  Card,
  Switch,
  Code,
  Divider,
  Loader,
  Accordion,
  Collapse,
  RingProgress,
  Highlight,
} from '@mantine/core';
import { IconCheck, IconDisc, IconPlus, IconSettings, IconTrash, IconX, IconChevronDown, IconChevronRight, IconDotsVertical, IconBan, IconCornerDownRightDouble } from '@tabler/icons';
import { SourceIcon } from '../../../../utils/SourceIcon';
import { showNotification } from '@mantine/notifications';
import { AvailableIntegration, BagelObjectConf, ExternalObjectConf, Integration } from '@bageltechnologies/bagel-common';
import { useIntegrationFields } from '../../../../api/hooks/useIntegrationFields';
import { useAuth0 } from '@auth0/auth0-react';
import SyncContext from '../../../../context/SyncContext';

// Business category icons
import { CustomerCallIcon, GeneralIcon, HealthNotesIcon, LostHoldReasonIcon, PmFeedbackIcon, RequestBusinessnameIcon, SupportCaseTicketIcon, SurveyIcon } from '../../../../icons/x-symbol-svgrepo-com';

interface BusinessCategory {
  businessName: string | null;
  businessCategory: string | null;
  icon: string;
}

interface NewMapping {
  externalField: string;
  bagelField: string;
}

interface FieldMappingDrawerProps {
  opened: boolean;
  onClose: () => void;
  selectedObject: ExternalObjectConf | null;
  selectedBagelObject: BagelObjectConf | null;
  integrationKey: string;
  integrationName: string;
  available?: AvailableIntegration;
  connected?: Integration;
  updateExternalObject: (originObject: string, updates: Partial<ExternalObjectConf>) => Promise<void>;
}

// Helper component for business category icons
const BusinessCategoryIcon: React.FC<{ iconName: string }> = ({ iconName }) => {
  const icons = [
    'bagel_lost_hold_reason',
    'bagel_Support_Case_Ticket',
    'bagel_Customer_Call',
    'bagel_survey',
    'bagel_Health_Notes',
    'bagel_pm_feedback',
    'bagel_request',
    'bagel_opportunity',
    'bagel_general',
    'bagel_interview'
  ];

  if (!iconName || !icons.includes(iconName)) {
    return <GeneralIcon />;
  }

  switch (iconName) {
  case 'bagel_lost_hold_reason':
    return <LostHoldReasonIcon />;
  case 'bagel_Support_Case_Ticket':
    return <SupportCaseTicketIcon />;
  case 'bagel_Customer_Call':
    return <CustomerCallIcon />;
  case 'bagel_survey':
    return <SurveyIcon />;
  case 'bagel_Health_Notes':
    return <HealthNotesIcon />;
  case 'bagel_pm_feedback':
    return <PmFeedbackIcon />;
  case 'bagel_request':
    return <RequestBusinessnameIcon />;
  case 'bagel_opportunity':
    return <RequestBusinessnameIcon />;
  case 'bagel_general':
    return <GeneralIcon />;
  default:
    return <GeneralIcon />;
  }
};

// SelectItem component with proper ref forwarding
interface BusinessCategoryItemProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label: string;
  icon: string;
  group?: string;
  query?: string;
}

const BusinessCategorySelectItem = forwardRef<HTMLDivElement, BusinessCategoryItemProps>(
  ({ value, label, icon, query, ...others }: BusinessCategoryItemProps, ref) => {
    return (
      <div ref={ref} {...others}>
        <Group noWrap>
          <Box w={20} h={20}>
            {icon && <BusinessCategoryIcon iconName={icon} />}
          </Box>
          <Highlight highlight={query || ''} size="sm">
            {label}
          </Highlight>
        </Group>
      </div>
    );
  }
);

BusinessCategorySelectItem.displayName = 'BusinessCategorySelectItem';

// External field select item with value displayed below label and metadata
interface ExternalFieldItemProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label: string;
  fillPercentage?: number;
  hasStatistics?: boolean;
  totalRecords?: number;
  nonNullCount?: number;
  types?: string[];
  required?: boolean;
  length?: number;
  query?: string;
}

const ExternalFieldSelectItem = forwardRef<HTMLDivElement, ExternalFieldItemProps>(
  ({ value, label, fillPercentage, hasStatistics, totalRecords, nonNullCount, types, required, length, query, ...others }: ExternalFieldItemProps, ref) => {
    // Prepare tooltip content
    const tooltipContent = useMemo(() => {
      const parts = [];
      if (types && types.length > 0) {
        parts.push(`Types: ${types.join(', ')}`);
      }
      if (totalRecords !== undefined && totalRecords !== null && nonNullCount !== undefined && nonNullCount !== null) {
        parts.push(`Records: ${nonNullCount.toLocaleString()}/${totalRecords.toLocaleString()}`);
      }
      if (required) {
        parts.push('Required field');
      }
      if (length !== undefined && length !== null) {
        parts.push(`Max length: ${length}`);
      }
      return parts.length > 0 ? parts.join(' | ') : null;
    }, [types, totalRecords, nonNullCount, required, length]);

    const content = (
      <div ref={ref} {...others}>
        <Group position="apart" align="center" noWrap>
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Highlight highlight={query || ''} size="sm">
              {label}
            </Highlight>
            {value !== label && (
              <Highlight highlight={query || ''} size="xs" color="dimmed">
                {value}
              </Highlight>
            )}
          </Stack>
          {fillPercentage !== undefined && fillPercentage !== null && (
            <RingProgress
              size={44}
              thickness={3}
              sections={[
                {
                  value: fillPercentage,
                  color: fillPercentage > 80 ? 'green' : fillPercentage > 50 ? 'yellow' : 'red'
                }
              ]}
              label={
                <Text size={8} align="center" weight={400} color={!fillPercentage ? 'dimmed': undefined}>
                  {fillPercentage ?`${Math.round(fillPercentage)}%` : 'N/A'}
                </Text>
              }
            />
          )}
        </Group>
      </div>
    );

    return content;
  }
);

ExternalFieldSelectItem.displayName = 'ExternalFieldSelectItem';

// Utility to convert a string to camelCase
function toCamelCase(str: string) {
  return str
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .replace(/(?:^|\s|_)([a-z])/g, (match, p1, offset) =>
      offset === 0 ? p1.toLowerCase() : p1.toUpperCase()
    )
    .replace(/\s+/g, '');
}

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Optimized filter function
const createOptimizedFilter = (searchQuery: string) => {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return () => true;
  
  return (value: string, item: any) => {
    const itemLabel = (item.label || '').toLowerCase();
    const itemValue = (item.value || '').toLowerCase();
    return itemLabel.includes(query) || itemValue.includes(query);
  };
};



// FieldSettingsBox component for field-specific settings
interface FieldSettingsBoxProps {
  field: any;
  updateField: (args: any) => Promise<any>;
  integrationKey: string;
  blacklistedFields: string[];
  onToggleBlacklist: (fieldKey: string, add: boolean) => void;
  isBlacklisting: boolean;
}

const FieldSettingsBox: React.FC<FieldSettingsBoxProps> = ({ field, updateField, integrationKey, blacklistedFields, onToggleBlacklist, isBlacklisting }) => {
  const [loading, setLoading] = React.useState(false);
  const [checked, setChecked] = React.useState(!!field.redactPII);

  React.useEffect(() => {
    setChecked(!!field.redactPII);
  }, [field.redactPII]);

  const isBlacklisted = blacklistedFields.includes(field.originName || field.name);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.checked;
    setLoading(true);
    setChecked(newValue);
    try {
      await updateField({
        fieldName: field.key || field.fieldPath,
        field: {
          ...field,
          fieldType: 'imported',
          redactPII: newValue,
          origin: integrationKey,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBlacklistToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleBlacklistToggle', field);
    const add = event.currentTarget.checked;
    onToggleBlacklist(field.originName || field.name, add);
  };

  return (
    <Stack spacing="xs">
      <Switch
        label="Redact PII"
        checked={checked}
        onChange={handleToggle}
        disabled={loading}
      />
      <Switch
        label="Blacklist this field"
        checked={isBlacklisted}
        onChange={handleBlacklistToggle}
        disabled={isBlacklisting}
        color="red"
      />
    </Stack>
  );
};

export const FieldMappingDrawer: React.FC<FieldMappingDrawerProps> = ({
  opened,
  onClose,
  selectedObject,
  selectedBagelObject,
  integrationKey,
  integrationName,
  available,
  connected,
  updateExternalObject
}) => {
  if (!integrationKey ||!selectedObject || !selectedBagelObject) return null;

  const auth0 = useAuth0();
  const [newMapping, setNewMapping] = useState<NewMapping | null>(null);
  const [showNewMappingRow, setShowNewMappingRow] = useState(false);
  const [fieldUpdating, setFieldUpdating] = useState<string | null>(null);
  const [extraParamsUpdating, setExtraParamsUpdating] = useState<string | null>(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string | null>(
    selectedObject?.extraParams?.businessName || null
  );
  const [selectKey, setSelectKey] = useState(Date.now());
  const [externalFieldSearch, setExternalFieldSearch] = useState('');
  const [bagelFieldSearch, setBagelFieldSearch] = useState('');
  const [customMappingSearches, setCustomMappingSearches] = useState<Record<string, string>>({});
  
  // Debounced search values
  const debouncedExternalFieldSearch = useDebounce(externalFieldSearch, 300);
  const debouncedBagelFieldSearch = useDebounce(bagelFieldSearch, 300);
  const debouncedCustomMappingSearches = useDebounce(customMappingSearches, 300);

  // Memoized filter functions for performance
  const externalFieldFilter = useMemo(() => 
    createOptimizedFilter(debouncedExternalFieldSearch), 
  [debouncedExternalFieldSearch]
  );
  
  const bagelFieldFilter = useMemo(() => 
    createOptimizedFilter(debouncedBagelFieldSearch), 
  [debouncedBagelFieldSearch]
  );
  
  const getCustomMappingFilter = useCallback((fieldKey: string) => {
    return createOptimizedFilter(debouncedCustomMappingSearches[fieldKey] || '');
  }, [debouncedCustomMappingSearches]);

  // Update selected business name when selectedObject changes
  React.useEffect(() => {
    if (selectedObject?.extraParams?.businessName) {
      setSelectedBusinessName(selectedObject.extraParams.businessName);
      
      // Force select to reinitialize with a slight delay
      setTimeout(() => {
        setSelectKey(Date.now());
      }, 50);
    } else {
      setSelectedBusinessName(null);
    }
  }, [selectedObject]);

  const {
    fields: object2Fields,
    blacklistedFields,
    isLoading: isFieldsLoading,
    updateField,
    updateFieldAsync,
    addOrRemoveBlacklistedFieldAsync,
    isBlacklisting,
  } = useIntegrationFields(integrationKey, selectedBagelObject.key, selectedObject.originObject, auth0);

  console.log('object2Fields', {selectedBagelObject: selectedBagelObject.key, originObject:selectedObject?.originObject || '', object2Fields});

  // Get available fields for mapping
  const initialBagelFields = useMemo(() => {    
    return selectedBagelObject.fields.map((field: any) => ({
      value: field.fieldPath,
      label: field.name,
      type: field.type,
      ...field
    }));
  }, [selectedBagelObject]);

  // Add state for manageable bagel fields
  const [availableBagelFields, setAvailableBagelFields] = useState<Array<any>>(initialBagelFields);

  // Update availableBagelFields when initialBagelFields changes
  React.useEffect(() => {
    setAvailableBagelFields(initialBagelFields);
  }, [initialBagelFields]);

  // Get initial external fields for mapping with enhanced metadata
  const initialExternalFields = useMemo(() => {
    return (selectedObject.fieldsMeta || []).map(field => ({
      ...field,
      value: field.key || (field as any).value,
      label: field.label || field.key || (field as any).value,
      fillPercentage: (field as any).fillPercentage,
      hasStatistics: (field as any).hasStatistics,
      totalRecords: (field as any).totalRecords,
      nonNullCount: (field as any).nonNullCount,
      types: (field as any).types,
      required: (field as any).required,
      length: (field as any).length
    }));
  }, [selectedObject]);

  // Add state for manageable external fields
  const [availableExternalFields, setAvailableExternalFields] = useState<Array<any>>(initialExternalFields);

  // Update availableExternalFields when initialExternalFields changes
  React.useEffect(() => {
    setAvailableExternalFields(initialExternalFields);
  }, [initialExternalFields]);

  // Get fields with disabled state for custom mapping
  const getFieldsWithDisabledState = (currentField: string) => {
    return availableBagelFields.map(field => ({
      ...field,
      disabled: (object2Fields || []).some((f: any) => {
        // Disable if this Bagel field is mapped to another external field (not the current one)
        return f.fieldKey !== currentField && f.fieldPath === field.value;
      })
    }));
  };

  // Handle field mapping change for custom mappings
  const handleFieldMappingChange = async (originField: string, key: string) => {
    console.log('handleFieldMappingChange', {selectedBagelObject, originField, key});
    // First try to find the field in selectedBagelObject.fields
    let field = selectedBagelObject.fields.find((f: any) => f.fieldPath === key);
    
    // If not found in original fields, look in our availableBagelFields state
    // which includes newly created fields
    if (!field) {
      const newField = availableBagelFields.find(f => f.value === key);
      if (newField) {
        // Create a compatible field object from our new field
        field = {
          ...newField,
          name: newField.label,
          fieldPath: newField.value,
          type: newField.type || 'string',
          fieldType: 'imported',
        };
      } else {
        console.error('Field not found:', key);
        return;
      }
    }
    
    console.log('handleFieldMappingChange field found:', field);

    try {
      setFieldUpdating(originField);
      await updateField({
        fieldName: key,
        field: {
          name: field?.name,
          fieldPath: field?.fieldPath,
          key: field?.fieldPath,
          fieldType: 'imported',
          type: field?.type,
          origin: integrationKey,
          originName: originField
        }
      });
      showNotification({
        title: `Field updated: ${originField}`,
        message: 'Field updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error:', error);
      showNotification({
        title: `Error updating field: ${originField}`,
        message: 'Error updating field',
        color: 'red'
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle extra mapping (idea/evidence) change
  const handleFieldMappingExtraChange = async (originField: string, fieldKey: string, extraKey: string, value: string) => {
    // Find the full field object as in handleFieldMappingChange
    console.log('handleFieldMappingExtraChange', {selectedBagelObject, originField, fieldKey, extraKey, value});
    const field = object2Fields.find((f: any) => f.fieldPath === fieldKey);

    if (!field) {
      console.error('Field not found:', fieldKey);
      return;
    }
    try {
      setFieldUpdating(originField);
      await updateField({
        fieldName: fieldKey,
        field: {
          ...field,
          fieldType: 'imported',
          [extraKey]: value,
        }
      });
    } catch (error) {
      console.error('Error updating extra mapping:', error);
      showNotification({
        title: `Error updating field: ${originField}`,
        message: 'Error updating field',
        color: 'red'
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle adding new mapping
  const handleAddNewMapping = () => {
    if (!newMapping?.externalField || !newMapping?.bagelField) return;
    handleFieldMappingChange(newMapping.externalField, newMapping.bagelField);
    setNewMapping(null);
    setShowNewMappingRow(false);
  };

  console.log('object2Fields', object2Fields);
  // Get mapped fields for custom mapping
  const customMappedFields = useMemo(() => {
    if (!object2Fields) return [];
    return object2Fields
      .filter((field: any) => field.originName)
      .map((field: any) => ({
        fieldPath: field.fieldPath,
        key: field.key,
        originName: field.originName,
        name: field.name,
        type: field.type
      }));
  }, [object2Fields]);

  console.log('available', available );

  let availableExternalObject = available?.externalObjects?.find((obj: any) => obj.bagelObjectKey == selectedObject.bagelObjectKey);
  if(!availableExternalObject || !availableExternalObject.fields) {
    availableExternalObject = connected?.externalObjects?.find((obj: any) => obj.bagelObjectKey == selectedObject.bagelObjectKey);
  }

  console.log('availableExternalObjects', availableExternalObject, selectedObject);
  const { businessCategoryMapping } = useContext(SyncContext);

  console.log('businessCategoryMapping', businessCategoryMapping);

  // Helper function to update extraParams
  const updateExtraParams = async (updates: Record<string, any>) => {
    if (!selectedObject) return;
    
    try {
      const extraParamsUpdates = {
        extraParams: {
          ...(selectedObject.extraParams || {}),
          ...updates
        }
      } as Partial<ExternalObjectConf>;
      
      await updateExternalObject(selectedObject.originObject, extraParamsUpdates);
      showNotification({
        title: `Settings updated`,
        message: 'Configuration updated successfully',
        color: 'green'
      });
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      showNotification({
        title: 'Error updating settings',
        message: 'Failed to update configuration',
        color: 'red'
      });
      return false;
    }
  };

  // Helper function to prepare business name select data
  const prepareBusinessNameData = () => {
    // Start with the filtered business categories
    const options = (businessCategoryMapping as BusinessCategory[])
      .filter((item) => item.businessName)
      .map((item) => ({
        value: item.businessName as string,
        label: item.businessName as string,
        group: item.businessCategory || 'Other',
        icon: item.icon
      }));
    
    // If current value exists but is not in options, add it
    const currentValue = selectedObject?.extraParams?.businessName;
    if (currentValue && !options.some(opt => opt.value === currentValue)) {
      console.log("Adding missing option:", currentValue);
      options.push({
        value: currentValue,
        label: currentValue,
        group: 'Custom',
        icon: 'default_icon'
      });
    }
    
    return options;
  };

  // Add state for tracking expanded state
  const [isFieldsExpanded, setIsFieldsExpanded] = useState(false);

  // Add state for tracking which row's settings are open
  const [openSettingsRow, setOpenSettingsRow] = useState<string | null>(null);

  const handleToggleBlacklist = (fieldKey: string, add: boolean) => {
    addOrRemoveBlacklistedFieldAsync({
      action: add ? 'add' : 'remove',
      field: fieldKey,
    });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      padding={0}
      position="right"
      size="lg"
      withCloseButton={false}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isFieldsLoading} overlayBlur={2} />
        <Box>
          <Box p="md" sx={(theme) => ({
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
            backgroundColor: theme.white,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          })}>
            <Group position="apart">
              <SourceIcon sourceName={integrationKey} width={20} height={20} />
              <Text weight={500}>
                {`Map Fields - ${selectedObject.label}`}
              </Text>
              <CloseButton onClick={onClose} />
            </Group>
          </Box>

                    
          {/* Extra configurations */}
          {(selectedBagelObject?.supportsIdeaCreation !== undefined || selectedBagelObject?.supportsEvidenceCreation !== undefined) && <Card withBorder shadow='sm' m="xs"  p={0}>
            <Group align="center" spacing={10} py={6} px="xs" bg="#f8f9fa">
              <IconSettings size={16} color="#868e96" />
              <Text size="xs" color="dimmed" weight={500}>Extra configurations</Text>
            </Group>
            <Divider />
            {/* Create linked Ideas */}
            {selectedBagelObject?.supportsIdeaCreation !== undefined && (
              <Group position="apart" p="xs">
                <Switch 
                  label="Create linked Ideas"
                  checked={selectedObject?.extraParams?.createLinkedIdeas || false}
                  onChange={async (event) => {
                  // Guard against null event or currentTarget
                    if (!event || !event.currentTarget || !selectedObject) return;
                    
                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('createLinkedIdeas');
                    await updateExtraParams({ createLinkedIdeas: isChecked })
                      .finally(() => {
                        setExtraParamsUpdating(null);
                      });
                  }}
                />
                {extraParamsUpdating == 'createLinkedIdeas' && <Loader size="xs" />}
              </Group>
            )}
            {/* Create linked Evidence */}
            {selectedBagelObject?.supportsEvidenceCreation !== undefined && (
              <Group position="apart" p="xs">
                <Switch 
                  label="Create linked Evidence"
                  checked={selectedObject?.extraParams?.createLinkedEvidence || false}
                  onChange={async (event) => {
                  // Guard against null event or currentTarget
                    if (!event || !event.currentTarget || !selectedObject) return;
                    
                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('createLinkedEvidence');
                    await updateExtraParams({ createLinkedEvidence: isChecked })
                      .finally(() => {
                        setExtraParamsUpdating(null);
                      });
                  }}
                />
                {extraParamsUpdating == 'createLinkedEvidence' && <Loader size="xs" />}
              </Group>
            )}
            {/* Import all external fields */}
            {integrationKey === 'salesforce' && <Group position="apart" p="xs">
              <Switch 
                label="Import all external fields"
                checked={selectedObject?.extraParams?.importAllFields || false}
                onChange={async (event) => {
                // Guard against null event or currentTarget
                  if (!event || !event.currentTarget || !selectedObject) return;
                  
                  const isChecked = event.currentTarget.checked;
                  setExtraParamsUpdating('importAllFields');
                  await updateExtraParams({ importAllFields: isChecked })
                    .finally(() => {
                      setExtraParamsUpdating(null);
                    });
                }}
              />
              {extraParamsUpdating == 'importAllFields' && <Loader size="xs" />}
            </Group>}
            {/* <Divider variant='dashed' /> */}
            {/* Business Name */}
            {/* <Group p="xs" spacing={0} position="apart">
              <Text size="sm" weight={500}>Business Name</Text>
              <Select
                key={selectKey}
                miw={250}
                data={prepareBusinessNameData()}
                value={selectedBusinessName || ''}
                rightSection={extraParamsUpdating == 'businessName' && <Loader size="xs" />  }
                onChange={async (value) => {
                  // Only trigger update if value actually changed
                  if (value === selectedBusinessName) return;
                    
                  if (!selectedObject) return;
                    
                  // Update local state immediately for better UX
                  setSelectedBusinessName(value);
                  setExtraParamsUpdating('businessName');
                  const success = await updateExtraParams({ businessName: value || '' })
                    .finally(() => {
                      setExtraParamsUpdating(null);
                    });
                    
                  // Revert state on error
                  if (!success) {
                    setSelectedBusinessName(selectedObject?.extraParams?.businessName || null);
                  }
                }}
                placeholder="Select business name"
                // searchable
                clearable
                withinPortal
                nothingFound="No matching business names"
                // onSearchChange={setSearchQuery}
                // searchValue={searchQuery}
                // filter={(value, item) => {
                //   const labelMatch = item.label?.toLowerCase().includes(value.toLowerCase().trim()) || false;
                //   const groupMatch = item.group?.toLowerCase().includes(value.toLowerCase().trim()) || false;
                //   return labelMatch || groupMatch;
                // }}
                itemComponent={BusinessCategorySelectItem}
              />
            </Group> */}
          </Card>}
          
          <ScrollArea>
            <Box>
              {/* Field Mapping Table with all fields in one accordion */}
              <Table>
                <thead style={{ borderTop: '1px solid #dee2e6' }}>
                  <tr>
                    <th style={{ width: '40%' }}>{integrationName} Field</th>
                    <th style={{ width: '52%', backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>Bagel Field</th>
                    <th style={{ width: '8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Default Auto-Mapped Fields in a Collapse */}
                  {(availableExternalObject && (availableExternalObject?.fields?.length || 0) > 0) && (
                    <>
                      <tr 
                        style={{ 
                          backgroundColor: '#5c5ceb0d',
                          cursor: 'pointer',
                          height: '56px' // Standard row height
                        }}
                        onClick={() => setIsFieldsExpanded(!isFieldsExpanded)}
                      >
                        <td colSpan={3}>
                          <Group spacing="xs">
                            {isFieldsExpanded 
                              ? <IconChevronDown size={16} /> 
                              : <IconChevronRight size={16} />}
                            <Text weight={500}>Default Field Mapping</Text>
                          </Group>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ padding: 0 }}>
                          <Collapse in={isFieldsExpanded}>
                            <Table>
                              <tbody>
                                {availableExternalObject && availableExternalObject?.fields?.map((field: any, index: number) => {
                                  const rowKey = `default-${index}`;
                                  const isDefaultBlacklisted = blacklistedFields.includes(field.fieldPath);
                                  return (
                                    <React.Fragment key={rowKey}>
                                      <tr style={{ height: '48px'}}>
                                        <td style={{ width: '40%' }}>
                                          <Group spacing={2}>
                                            {isDefaultBlacklisted && (
                                              <Tooltip label="This field is blacklisted" withArrow>
                                                <span style={{ marginRight: 8, verticalAlign: 'middle', color: 'red', display: 'inline-flex', alignItems: 'center' }}>
                                                  <IconBan size={18} />
                                                </span>
                                              </Tooltip>
                                            )}
                                            <Stack spacing={2}>
                                              <Text size="sm" weight={500}>
                                                {field.fieldPath || `Default value: ${field.defaultValue}`}
                                              </Text>
                                              <Tooltip label="Type" withArrow>
                                                <Text size="xs" color="dimmed">
                                                  {field.type}
                                                </Text>
                                              </Tooltip>
                                            </Stack>
                                          </Group>
                                        </td>
                                        <td style={{ width: '52%', backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                                          <Select
                                            data={[field.key || field.name]}
                                            value={field.key || field.name}
                                            disabled
                                            withinPortal
                                            itemComponent={({ query, ...props }) => (
                                              <ExternalFieldSelectItem {...props} query={query} />
                                            )}
                                          />
                                        </td>
                                        <td style={{ width: '8%', textAlign: 'center', position: 'relative' }}>
                                          <Tooltip withArrow label="Configure" withinPortal>
                                            <ActionIcon onClick={e => { e.stopPropagation(); setOpenSettingsRow(openSettingsRow === rowKey ? null : rowKey); }}>
                                              <IconDotsVertical size={18} />
                                            </ActionIcon>
                                          </Tooltip>
                                        </td>
                                      </tr>
                                      {/* Collapsible settings row */}
                                      {openSettingsRow === rowKey && (
                                        <tr>
                                          <td colSpan={3} style={{ background: '#f8f9fa', padding: 0 }}>
                                            <Collapse in={true}>
                                              <Box p="sm"
                                                sx={{
                                                  boxShadow: 'inset 0 8px 12px -8px rgba(0,0,0,0.10), inset 0 -8px 12px -8px rgba(0,0,0,0.10)'
                                                }}
                                              >
                                                <FieldSettingsBox
                                                  field={field}
                                                  updateField={updateFieldAsync}
                                                  integrationKey={integrationKey}
                                                  blacklistedFields={blacklistedFields}
                                                  onToggleBlacklist={handleToggleBlacklist}
                                                  isBlacklisting={isBlacklisting}
                                                />
                                              </Box>
                                            </Collapse>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </Collapse>
                        </td>
                      </tr>
                    </>
                  )}
                  {/* Custom Mapped Fields (editable select) */}
                  {customMappedFields?.map((field: any, index: number) => {
                    const rowKey = `custom-${field.originName}-${index}`;
                    const externalField = selectedObject.fieldsMeta?.find(f => f.key === field.originName);
                    const isBlacklisted = blacklistedFields.includes(field.originName);
                    // Find the latest field from object2Fields by fieldKey or originName
                    const latestField = object2Fields?.find((f: any) => f.fieldKey === field.fieldKey || f.originName === field.originName) || field;
                    // Determine if we need to show the extra select(s)
                    const mappingTypes = [
                      {
                        key: 'idea',
                        enabled: !!selectedObject?.extraParams?.createLinkedIdeas,
                        label: 'Idea',
                        tooltip: 'Pick an Idea field to map to on creation',
                        options: [
                          { value: 'summary', label: 'Title' },
                          { value: 'description', label: 'Description' },
                          { value: 'description1', label: 'Description1' },
                          { value: 'description2', label: 'Description2' },
                          { value: 'description3', label: 'Description3' },
                          { value: 'description4', label: 'Description4' },
                          { value: 'description5', label: 'Description5' },
                          { value: 'description6', label: 'Description6' },
                          { value: 'status', label: 'Status' },
                          { value: 'priority', label: 'Priority' },
                          { value: 'labels', label: 'Origin Tags' },
                          { value: 'originType', label: 'Origin Type' },
                          { value: 'componentText', label: 'Component Text' },
                          { value: 'components', label: 'Components' },
                          { value: 'itemStatus', label: 'Item Status' },
                          { value: 'createdAt', label: 'Created At' },
                          { value: 'updatedAt', label: 'Updated At' },
                        ],
                        fieldKey: 'ideaFieldMapping',
                      },
                      {
                        key: 'evidence',
                        enabled: !!selectedObject?.extraParams?.createLinkedEvidence,
                        label: 'Evidence',
                        tooltip: 'Pick an Evidence field to map to on creation',
                        options: [
                          { value: 'title', label: 'Title' },
                          { value: 'text', label: 'Text' },
                          { value: 'accountId', label: 'Account ID' },
                          { value: 'oneLineSummary', label: 'One Line Summary' },
                          { value: 'priority', label: 'Priority' },
                          { value: 'type', label: 'Type' },
                          { value: 'componentString', label: 'Component String' },
                          { value: 'createdAt', label: 'Created At' },
                          { value: 'updatedAt', label: 'Updated At' },
                        ],
                        fieldKey: 'evidenceFieldMapping',
                      },
                    ];
                    // Get current value for selects from field (if exists)
                    const ideaFieldMapping = field.ideaFieldMapping || '';
                    const evidenceFieldMapping = field.evidenceFieldMapping || '';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr style={{ height: '48px' }}>
                          <td style={{}}>
                            <Group spacing={2}>
                              {isBlacklisted && (
                                <Tooltip label="This field is blacklisted" withArrow>
                                  <span style={{ marginRight: 8, verticalAlign: 'middle', color: 'red', display: 'inline-flex', alignItems: 'center' }}>
                                    <IconBan size={18} />
                                  </span>
                                </Tooltip>
                              )}
                              <Stack spacing={2}>
                                <Text size="sm" weight={500}>
                                  {externalField?.label || field.originName}
                                </Text>
                                <Tooltip label="Type" withArrow>
                                  <Text size="xs" color="dimmed">
                                    {externalField?.type || field.type}
                                  </Text>
                                </Tooltip>
                              </Stack>
                            </Group>
                          </td>
                          <td style={{ backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                            <Stack spacing={4}>
                              <Select
                                data={[{value: field.key || '', label: field.key || ''}, ...getFieldsWithDisabledState(field.originName || '') || [] ]}
                                value={field.key}
                                onChange={(value) => handleFieldMappingChange(field.originName || '', value || '')}
                                placeholder="Select field"
                                clearable
                                searchable
                                withinPortal
                                creatable
                                itemComponent={({ query, ...props }) => (
                                  <ExternalFieldSelectItem {...props} query={customMappingSearches[field.originName || ''] || ''} />
                                )}
                                getCreateLabel={(query) => `+ Create \"${query}\"`}
                                onCreate={(query) => {
                                  const newField = { value: query, label: query };
                                  setAvailableBagelFields((prev) => {
                                    if (prev.some(field => field.value === query)) {
                                      return prev;
                                    }
                                    return [...prev, newField];
                                  });
                                  return query;
                                }}
                                filter={getCustomMappingFilter(field.originName || '')}
                                searchValue={customMappingSearches[field.originName || ''] || ''}
                                onSearchChange={(query) => {
                                  setCustomMappingSearches(prev => ({
                                    ...prev,
                                    [field.originName || '']: query
                                  }));
                                }}
                              />
                              {/* Extra select(s) for idea/evidence mapping, rendered separately */}
                              {selectedObject?.extraParams?.createLinkedIdeas && (
                                <Group spacing={6} align="center">
                                  <Tooltip label="Pick an Idea field to map to on creation" withArrow>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <IconCornerDownRightDouble size={22} style={{ marginRight: 4, color: '#5c5ceb' }} />
                                    </span>
                                  </Tooltip>
                                  <Select
                                    data={mappingTypes.find(mt => mt.key === 'idea')?.options || []}
                                    value={latestField.ideaFieldMapping || ''}
                                    onChange={async (value) => {
                                      await handleFieldMappingExtraChange(field.originName, field.key, 'ideaFieldMapping', value || '');
                                    }}
                                    placeholder="Map to Idea field"
                                    withinPortal
                                    styles={{ input: { minWidth: 140 } }}
                                    creatable
                                    searchable
                                    getCreateLabel={(query) => `+ Create "${query}"`}
                                    onCreate={(query) => {
                                      const mt = mappingTypes.find(mt => mt.key === 'idea');
                                      if (mt) mt.options.push({ value: query, label: query });
                                      return query;
                                    }}
                                  />
                                </Group>
                              )}
                              {selectedObject?.extraParams?.createLinkedEvidence && (
                                <Group spacing={6} align="center">
                                  <Tooltip label="Pick an Evidence field to map to on creation" withArrow>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <IconCornerDownRightDouble size={22} style={{ marginRight: 4, color: '#5c5ceb' }} />
                                    </span>
                                  </Tooltip>
                                  <Select
                                    data={mappingTypes.find(mt => mt.key === 'evidence')?.options || []}
                                    value={latestField.evidenceFieldMapping || ''}
                                    onChange={async (value) => {
                                      await handleFieldMappingExtraChange(field.originName, field.key, 'evidenceFieldMapping', value || '');
                                    }}
                                    placeholder="Map to Evidence field"
                                    withinPortal
                                    styles={{ input: { minWidth: 140 } }}
                                    creatable
                                    getCreateLabel={(query) => `+ Create "${query}"`}
                                    onCreate={(query) => {
                                      const mt = mappingTypes.find(mt => mt.key === 'evidence');
                                      if (mt) mt.options.push({ value: query, label: query });
                                      return query;
                                    }}
                                  />
                                </Group>
                              )}
                            </Stack>
                          </td>
                          <td style={{ textAlign: 'center', position: 'relative' }}>
                            <Tooltip withArrow label="Configure" withinPortal>
                              <ActionIcon onClick={() => setOpenSettingsRow(openSettingsRow === rowKey ? null : rowKey)}>
                                <IconDotsVertical size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </td>
                        </tr>
                        {/* Collapsible settings row */}
                        {openSettingsRow === rowKey && (
                          <tr>
                            <td colSpan={3} style={{ background: '#f8f9fa', padding: 0 }}>
                              <Collapse in={true}>
                                <Box p="sm"
                                  sx={{
                                    boxShadow: 'inset 0 8px 12px -8px rgba(0,0,0,0.10), inset 0 -8px 12px -8px rgba(0,0,0,0.10)'
                                  }}
                                >
                                  <FieldSettingsBox
                                    field={field}
                                    updateField={updateFieldAsync}
                                    integrationKey={integrationKey}
                                    blacklistedFields={blacklistedFields}
                                    onToggleBlacklist={handleToggleBlacklist}
                                    isBlacklisting={isBlacklisting}
                                  />
                                </Box>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* New Mapping Row - shown outside the accordion */}
                  {showNewMappingRow && (
                    (() => {
                      let bagelFieldOptions = availableBagelFields;
                      if (
                        newMapping?.bagelField &&
                        !availableBagelFields.some(f => f.value === newMapping.bagelField)
                      ) {
                        bagelFieldOptions = [
                          { value: newMapping.bagelField, label: newMapping.bagelField },
                          ...availableBagelFields
                        ];
                      }
                      return (
                        <tr style={{ height: '48px' }}>
                          <td style={{ width: '40%' }}>
                            <Select
                              data={availableExternalFields.map(f => ({ 
                                value: f.key || f.value, 
                                label: f.label,
                                fillPercentage: f.fillPercentage,
                                hasStatistics: f.hasStatistics,
                                totalRecords: f.totalRecords,
                                nonNullCount: f.nonNullCount,
                                types: f.types,
                                required: f.required,
                                length: f.length
                              }))}
                              value={newMapping?.externalField}
                              onChange={(value) => {
                                setNewMapping(value ? {
                                  externalField: value,
                                  bagelField: newMapping?.bagelField || ''
                                } : null);
                              }}
                              placeholder="Select external field"
                              searchable
                              clearable
                              withinPortal
                              creatable
                              itemComponent={({ query, ...props }) => (
                                <ExternalFieldSelectItem {...props} query={externalFieldSearch} />
                              )}
                              getCreateLabel={(query) => `+ Create \"${query}\"`}
                              onCreate={(query) => {
                                const newField = { key: query, label: query, value: query };
                                setAvailableExternalFields((prev) => {
                                  if (prev.some(field => field.key === query)) {
                                    return prev;
                                  }
                                  return [...prev, newField];
                                });
                                setNewMapping({
                                  externalField: query,
                                  bagelField: newMapping?.bagelField || ''
                                });
                                return query;
                              }}
                              filter={externalFieldFilter}
                              searchValue={externalFieldSearch}
                              onSearchChange={setExternalFieldSearch}
                            />
                          </td>
                          <td style={{ width: '52%', backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                            <Group position="apart" spacing={0} noWrap>
                              <ActionIcon
                                color="gray"
                                variant="outline"
                                onClick={() => {
                                  setShowNewMappingRow(false);
                                  setNewMapping(null);
                                }}
                                w={36}
                                h={36}
                                sx={{
                                  borderRadius: '4px 0 0 4px',
                                  borderColor: '#cfd4da',
                                  borderRight: 'none',
                                }}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                              <Select
                                styles={{
                                  input: {
                                    borderRadius: 0,
                                  },
                                }}
                                data={bagelFieldOptions}
                                value={newMapping?.bagelField}
                                onChange={(value) => setNewMapping(value ? {
                                  bagelField: value,
                                  externalField: newMapping?.externalField || ''
                                } : null)}
                                placeholder="Select Bagel field"
                                searchable
                                clearable
                                withinPortal
                                creatable
                                itemComponent={({ query, ...props }) => (
                                  <ExternalFieldSelectItem {...props} query={bagelFieldSearch} />
                                )}
                                getCreateLabel={(query) => `+ Create \"${query}\"`}
                                onCreate={(query) => {
                                  const newField = { value: query, label: query };
                                  setAvailableBagelFields((prev) => {
                                    if (prev.some(field => field.value === query)) {
                                      return prev;
                                    }
                                    return [...prev, newField];
                                  });
                                  return query;
                                }}
                                filter={bagelFieldFilter}
                                searchValue={bagelFieldSearch}
                                onSearchChange={setBagelFieldSearch}
                              />
                              <ActionIcon
                                sx={{
                                  borderRadius: '0 4px 4px 0',
                                }}
                                color="indigo"
                                variant="filled"
                                onClick={handleAddNewMapping}
                                disabled={!newMapping?.externalField || !newMapping?.bagelField}
                                w={36}
                                h={36}
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                            </Group>
                          </td>
                          <td style={{width: '8%'}}></td>
                        </tr>
                      );
                    })()
                  )}
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td colSpan={3}>
                      <Button
                        variant="outline"
                        color="indigo"
                        fullWidth
                        size="xs"
                        onClick={() => setShowNewMappingRow(true)}
                        disabled={showNewMappingRow}
                        leftIcon={<IconPlus size={16} />}
                      >
                        Add Field Mapping
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Box>
          </ScrollArea>
        </Box>
      </Box>
    </Drawer>
  );
};